import { useEffect, useState } from 'react';

import { useReducedMotion } from '@/shared/hooks/useReducedMotion';

/** Between one row taking its new state and the next (spec 105 research R6). */
export const STAGGER_MS = 80;

/** The whole sequence never runs longer than this, however many rows there are. */
export const STAGGER_CEILING_MS = 1_000;

/**
 * How many of a list have arrived, one after another (spec 105 FR-015).
 *
 * The Library's one piece of choreography: when an install reports what it did,
 * the dependency rows take their new state in turn rather than all at once, so
 * a reader watches the thing they asked for happen instead of reading a summary
 * of it afterwards. Nothing moves and nothing enters — each row is already on
 * screen and only its right-hand side changes.
 *
 * 80 ms is below the point at which steps stop reading as separate events and
 * above the point at which six of them finish before the eye arrives. The
 * ceiling matters because a workflow may name many dependencies: six rows take
 * under half a second, but twenty at the same interval would stop being an
 * answer and become a wait, so the interval shortens instead.
 *
 * Reduced motion is not a shorter version of this: it is the whole list, from
 * the first render, with no timer started at all (FR-017).
 *
 * The count is derived during render rather than set from the effect — the
 * first row is there as soon as the result is, and only the rows after it need
 * a timer. Standing the sequence down resets it in render too, the same
 * previous-state adjustment the Library's search field uses.
 */
export function useStaggered(total: number, active: boolean): number {
  const reduced = useReducedMotion();
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(active);
  if (running !== active) {
    setRunning(active);
    setElapsed(0);
  }

  useEffect(() => {
    if (!active || reduced || total <= 1) return;
    const step = Math.min(STAGGER_MS, STAGGER_CEILING_MS / total);
    let arrived = 0;
    const timer = setInterval(() => {
      arrived += 1;
      setElapsed(arrived);
      if (arrived >= total - 1) clearInterval(timer);
    }, step);
    return () => {
      clearInterval(timer);
    };
  }, [active, reduced, total]);

  if (!active) return 0;
  if (reduced) return total;
  return Math.min(elapsed + 1, total);
}
